import React, { useEffect, useState } from "react";
import { getFirestore, collection, getDocs, getDoc, setDoc, doc, deleteDoc, updateDoc, query, onSnapshot, orderBy, addDoc, serverTimestamp, where } from "firebase/firestore";
import app from "./firebase";
import { useCookies } from 'react-cookie';
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet";
import {
  LogOut, Menu, X, Check, XCircle, Bell, AlertTriangle, Send, Link, UserPlus, Users, BadgeCheck, ChevronDown, UserCheck, Lock, BookOpen,
  LayoutDashboard, History, Wallet, Settings, Store, PhoneCall, AlertCircle, CheckCircle2, Leaf, MessageSquare, Plus, Globe, Trash2
} from "lucide-react";

const db = getFirestore(app);

export default function MarketPlace() {
  const [name, setName] = useState("Investor");
  const [wallet, setWallet] = useState("0.00");
  const [userStats, setUserStats] = useState({}); // Cache for user reputation stats
  const [items, setItems] = useState([]);
  const [incomingBids, setIncomingBids] = useState([]);
  const [sentBids, setSentBids] = useState([]);
  const [activeTransactions, setActiveTransactions] = useState([]);
  const [activeProfile, setActiveProfile] = useState("Buyer"); // Buyer or Seller
  const [showModal, setShowModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTx, setActiveTx] = useState(null);
  const [show_mobile_menu, setMobileMenu] = useState(false);
  const [toast, setToast] = useState(null);
  const [unreadBidsCount, setUnreadBidsCount] = useState(0);
  const [cookies, , removeCookie] = useCookies(['username', 'name']);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeTx, setDisputeTx] = useState(null);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeDescription, setDisputeDescription] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAgent, setIsAgent] = useState(false);
  const [agentStatus, setAgentStatus] = useState("");
  const [agentId, setAgentId] = useState("");
  const [managedClients, setManagedClients] = useState([]);
  const [activeActingAs, setActiveActingAs] = useState(null); // The userId we are currently acting as
  const [showRegisterClientModal, setShowRegisterClientModal] = useState(false);
  const [txTab, setTxTab] = useState('active'); // 'active' or 'history'
  const [emailVerified, setEmailVerified] = useState(true);

  const [form, setForm] = useState({
    product: "",
    quantityValue: "",
    quantityUnit: "Tons",
    unitPrice: "",
    price: "",
    quantity: "",
    paymentMode: "Agrivestia Escrow",
    paymentTerm: "",
    deliveryType: "",
    deliveryDays: "",
    description: "",
    buyer_contact: "",
    buyer_contact_type: "Email",
    escrowAmount: "0",
    status: "0"
  });

  const userId = cookies.username;

  const showToast = (type, title, msg) => {
    setToast({ type, title, msg });
    setTimeout(() => setToast(null), 5000);
  };

  const sendTransactionEmail = async (uId, uName, productName, status) => {
    try {
      const uSnap = await getDoc(doc(db, "users", uId));
      if (!uSnap.exists()) return;
      const uData = uSnap.data();
      const uEmail = uData.email;
      if (!uEmail) return;

      const notifSnap = await getDoc(doc(db, "notification", uId));
      if (notifSnap.exists() && notifSnap.data().transaction) {
        const templateParams = {
          header: "AGRIVESTIA TRANSACTION",
          subject: `TRANSACTION ${status.toUpperCase()}`,
          message: `Hi ${uName}, your transaction for "${productName}" has been marked as ${status}. Check your marketplace dashboard for details.`,
          email: uEmail
        };
        await emailjs.send("service_pehhhv8", "template_rql77hn", templateParams, "9o1vflADP-wePeEFd");
      }
    } catch (err) { console.error("Email error:", err); }
  };

  const currentUserId = activeActingAs || userId;

  const initiateData = () => {
    if (!userId) return () => { };

    // 0. Fetch Agent Status & Managed Clients (Always for the main user)
    const unsubAgent = onSnapshot(doc(db, "users", userId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsAgent(data.isAgent || false);
        setAgentId(data.agentId || "");
        setAgentStatus(data.agentStatus || "");
        // If agent is not approved, clear actingAs context
        if (data.agentStatus !== "approved") {
          setActiveActingAs(null);
        }
      }
    });

    const qClients = query(collection(db, "users"), where("managedBy", "==", userId));
    const unsubClients = onSnapshot(qClients, (snapshot) => {
      const clients = snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
      setManagedClients(clients);
    });

    // 1. Listen to Current Active Profile (Wallet, Name, etc)
    const unsubUser = onSnapshot(doc(db, "users", currentUserId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setName(data.name || "User");
        setUserEmail(data.email || "");
        setWallet((data.wallet || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,'));
        if (data.activeProfile) setActiveProfile(data.activeProfile);
        setEmailVerified(data.emailVerified !== false);
      }
    });

    const qRequests = query(collection(db, "buyers_request"));
    const unsubRequests = onSnapshot(qRequests, (snapshot) => {
      const allItems = snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
      const managedClientIds = managedClients.map(c => c.id);
      setItems(allItems.filter(item =>
        item.buyerId === currentUserId ||
        (!activeActingAs && managedClientIds.includes(item.buyerId))
      ));
    });

    // 3. Listen to Proposals (Incoming and Sent)
    const qProposals = query(collection(db, "market_proposals"));
    const unsubProposals = onSnapshot(qProposals, (snapshot) => {
      const allProposals = snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
      const managedClientIds = managedClients.map(c => c.id);

      const userIncomingBids = allProposals.filter(p =>
        p.buyerId === currentUserId ||
        (!activeActingAs && managedClientIds.includes(p.buyerId))
      );
      setIncomingBids(userIncomingBids);
      setUnreadBidsCount(userIncomingBids.filter(b => !b.isRead).length);

      setSentBids(allProposals.filter(p =>
        p.sellerId === currentUserId ||
        (!activeActingAs && managedClientIds.includes(p.sellerId))
      ));
    });

    // 4. Listen to Active Transactions
    const qTransactions = query(collection(db, "market_transactions"));
    const unsubTransactions = onSnapshot(qTransactions, (snapshot) => {
      const allTxs = snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
      const managedClientIds = managedClients.map(c => c.id);

      setActiveTransactions(allTxs.filter(t =>
        t.buyerId === currentUserId ||
        t.sellerId === currentUserId ||
        (!activeActingAs && (managedClientIds.includes(t.buyerId) || managedClientIds.includes(t.sellerId)))
      ));
    });

    // 5. Build User Stats Cache (Reputation)
    const qUsersStats = query(collection(db, "users"));
    const unsubUsersStats = onSnapshot(qUsersStats, (snapshot) => {
      const statsMap = {};
      snapshot.forEach(doc => {
        const d = doc.data();
        statsMap[doc.id] = {
          successful: d.successfulTransactions || 0,
          cancelled: d.cancelledTransactions || 0
        };
      });
      setUserStats(statsMap);
    });

    return () => {
      unsubAgent();
      unsubClients();
      unsubUser();
      unsubRequests();
      unsubProposals();
      unsubTransactions();
      unsubUsersStats();
    };
  };

  const calculateSuccessRate = (uId) => {
    const stats = userStats[uId];
    if (!stats || (stats.successful === 0 && stats.cancelled === 0)) return null;
    const total = stats.successful + stats.cancelled;
    const rate = Math.round((stats.successful / total) * 100);
    return rate;
  };

  useEffect(() => {
    if (show_mobile_menu || showNotifications) document.body.classList.add("lock-scroll");
    else document.body.classList.remove("lock-scroll");
    return () => document.body.classList.remove("lock-scroll");
  }, [show_mobile_menu, showNotifications]);

  useEffect(() => {
    const unsubscribe = initiateData();
    return () => unsubscribe && unsubscribe();
  }, [userId, activeActingAs, managedClients.length]);

  if (!userId) {
    window.location.href = "/login";
    return null;
  }

  const switchProfile = async (profile) => {
    try {
      setActiveProfile(profile);
      await updateDoc(doc(db, "users", userId), { activeProfile: profile });
      showToast("success", "Profile Switched", `You are now interacting as a ${profile}.`);
    } catch (error) { console.error(error); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...form, [name]: value };

    // Auto-calculate Escrow only if mode is Agrivestia Escrow
    if (name === "quantityValue" || name === "unitPrice" || name === "paymentMode") {
      const gv = parseFloat(name === "quantityValue" ? value : form.quantityValue) || 0;
      const up = parseFloat(name === "unitPrice" ? value : form.unitPrice) || 0;
      const pm = name === "paymentMode" ? value : form.paymentMode;

      if (pm === "Agrivestia Escrow") {
        updatedForm.escrowAmount = (gv * up).toString();
      } else {
        updatedForm.escrowAmount = "0";
      }
    }

    setForm(updatedForm);
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request? The escrowed funds will be returned to your wallet.")) return;
    try {
      const itemToRefund = items.find(item => item.id === id);
      if (itemToRefund && itemToRefund.escrowAmount) {
        const refundAmt = parseFloat(itemToRefund.escrowAmount);
        if (!isNaN(refundAmt) && refundAmt > 0) {
          const currentWalletRaw = parseFloat(wallet.replaceAll(",", ""));
          const newVal = currentWalletRaw + refundAmt;
          await updateDoc(doc(db, "users", userId), { wallet: newVal });
          setWallet((newVal).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,'));
        }
      }
      await deleteDoc(doc(db, 'buyers_request', id));
      setItems(prev => prev.filter(i => i.id !== id));
      showToast("success", "Request Deleted", "Escrowed funds have been returned to your wallet.");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to delete request.");
    }
  };

  const addItem = async () => {
    if (!form.product || !form.quantityValue || !form.unitPrice || isSubmitting) {
      if (!isSubmitting) showToast("error", "Missing Info", "Please fill in all required fields.");
      return;
    }
    if (!emailVerified) { showToast("error", "Email Unverified", "Please verify your email address to perform this action."); return; }

    setIsSubmitting(true);
    const uniqueId = `${currentUserId}_${Date.now()}`;

    // Format legacy strings for Market display
    const legacyQuantity = `${form.quantityValue} ${form.quantityUnit}`;
    const legacyPrice = `₦${(parseFloat(form.unitPrice) * parseFloat(form.quantityValue)).toLocaleString()} Total (@ ₦${parseFloat(form.unitPrice).toLocaleString()}/${form.quantityUnit.replace('s', '')})`;

    const buyerName = activeActingAs
      ? `Agent (${agentId}) for Client`
      : name;

    const payload = {
      ...form,
      id: uniqueId,
      buyer: buyerName,
      buyerId: currentUserId,
      escrowAmount: form.paymentMode === "Agrivestia Escrow" ? (parseFloat(form.unitPrice) * parseFloat(form.quantityValue)).toString() : "0",
      time: new Date().toLocaleDateString(),
      quantity: legacyQuantity,
      price: legacyPrice,
      status: "0"
    };

    try {
      // If Escrow, check balance of the context user (currentUserId)
      if (form.paymentMode === "Agrivestia Escrow") {
        const userRef = doc(db, "users", currentUserId);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        const balance = userData.wallet || 0;
        const totalCost = parseFloat(form.unitPrice) * parseFloat(form.quantityValue);

        if (balance < totalCost) {
          showToast("error", "Insufficient Funds", `The wallet for ${activeActingAs ? "this client" : "you"} does not have enough funds to cover the escrow. (Required: ₦${totalCost.toLocaleString()})`);
          setIsSubmitting(false);
          return;
        }

        // Lock funds
        await updateDoc(userRef, { wallet: balance - totalCost });
      }

      await setDoc(doc(db, "buyers_request", uniqueId), payload);
      setShowModal(false);
      setForm({
        product: "", quantityValue: "", quantityUnit: "Tons", unitPrice: "", price: "", quantity: "",
        paymentMode: "Agrivestia Escrow", paymentTerm: "", deliveryType: "", deliveryDays: "",
        description: "", buyer_contact: "", buyer_contact_type: "Email", escrowAmount: "0", status: "0"
      });
      showToast("success", "Success", "Request posted successfully.");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to post request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const encodeId = (id) => {
    try {
      const salt = "agv_secure_2026";
      const str = `${id}|${salt}`;
      return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return id; }
  };

  const copyRequestLink = (requestId) => {
    const secureId = encodeId(requestId);
    const link = `${window.location.origin}/central_market?id=${secureId}`;
    navigator.clipboard.writeText(link);
    showToast("success", "Secure Link Copied", "Encrypted request link copied! Share this safely.");
  };

  const rejectBid = async (bidId) => {
    if (!window.confirm("Reject this offer?")) return;
    try {
      await deleteDoc(doc(db, 'market_proposals', bidId));
      setIncomingBids(prev => prev.filter(b => b.id !== bidId));
      showToast("success", "Offer Rejected", "The proposal has been removed.");
    } catch (error) { console.error(error); }
  };

  const acceptBid = async (requestId, bidId) => {
    try {
      if (!emailVerified) { showToast("error", "Email Unverified", "Please verify your email address to perform this action."); return; }
      const bid = incomingBids.find(b => b.id === bidId);
      const request = items.find(r => r.id === requestId);

      const bidPrice = parseFloat(bid.priceOffer.replace(/[^0-9.]/g, '')) || 0;
      const initialEscrow = parseFloat(request.escrowAmount) || 0;
      const isEscrow = request.paymentMode === "Agrivestia Escrow";
      const topUpNeeded = isEscrow ? (bidPrice - initialEscrow) : 0;
      const currentWallet = parseFloat(wallet.replaceAll(",", ""));

      if (isEscrow && topUpNeeded > 0) {
        if (currentWallet < topUpNeeded) {
          showToast("error", "Insufficient Funds", `You need an extra ₦${topUpNeeded.toLocaleString()} in your wallet to accept this bid.`);
          return;
        }
        if (!window.confirm(`This bid (₦${bidPrice.toLocaleString()}) is higher than your initial escrow (₦${initialEscrow.toLocaleString()}). ₦${topUpNeeded.toLocaleString()} will be deducted from your wallet to balance the transaction. Continue?`)) return;
      } else {
        const confirmMsg = isEscrow ? "Accept this offer? This will start a secure transaction." : `Accept this ${request.paymentMode} offer? No funds will be locked in escrow.`;
        if (!window.confirm(confirmMsg)) return;
      }

      // Identify Agents
      let buyerAgentId = null;
      let sellerAgentId = null;

      const buyerRef = doc(db, "users", userId);
      const buyerSnap = await getDoc(buyerRef);
      if (buyerSnap.exists()) buyerAgentId = buyerSnap.data().managedBy || null;

      const sellerRef = doc(db, "users", bid.sellerId);
      const sellerSnap = await getDoc(sellerRef);
      if (sellerSnap.exists()) sellerAgentId = sellerSnap.data().managedBy || null;

      const txId = `tx_${Date.now()}`;
      const transactionData = {
        id: txId,
        requestId,
        bidId,
        product: request.product,
        amount: bidPrice.toString(),
        buyerId: userId,
        buyerName: name,
        sellerId: bid.sellerId,
        sellerName: bid.sellerName,
        buyerAgentId,
        sellerAgentId,
        paymentMode: request.paymentMode,
        isEscrow,
        status: "Active",
        createdAt: new Date().toISOString(),
        escrowReleased: false,
        lastRead: { [userId]: new Date().toISOString() }
      };

      if (isEscrow) {
        if (topUpNeeded > 0) {
          await updateDoc(doc(db, "users", userId), { wallet: currentWallet - topUpNeeded });
          setWallet((currentWallet - topUpNeeded).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,'));
        }

        await addDoc(collection(db, "market_transactions", txId, "messages"), {
          senderId: "SYSTEM",
          senderName: "Agrivestia System",
          text: `🤝 Transaction started. ₦${bidPrice.toLocaleString()} locked in escrow.`,
          timestamp: new Date().toISOString(),
          isSystem: true
        });
      } else {
        await addDoc(collection(db, "market_transactions", txId, "messages"), {
          senderId: "SYSTEM",
          senderName: "Agrivestia System",
          text: `🤝 ${request.paymentMode} Transaction started. No funds are locked in escrow.`,
          timestamp: new Date().toISOString(),
          isSystem: true
        });
      }

      await setDoc(doc(db, "market_transactions", txId), transactionData);
      await updateDoc(doc(db, "market_transactions", txId), { lastMessageAt: new Date().toISOString() });

      await deleteDoc(doc(db, 'market_proposals', bidId));
      await deleteDoc(doc(db, 'buyers_request', requestId));

      setItems(prev => prev.filter(i => i.id !== requestId));
      setIncomingBids(prev => prev.filter(b => b.requestId !== requestId));
      setActiveTransactions(prev => [...prev, transactionData]);

      showToast("success", "Bid Accepted", "Escrow balanced and transaction ticket created.");
    } catch (error) { console.error(error); }
  };

  const releaseFunds = async (tx) => {
    if (!emailVerified) { showToast("error", "Email Unverified", "Please verify your email address to perform this action."); return; }
    if (!window.confirm(`Release ₦${parseFloat(tx.amount).toLocaleString()} to ${tx.sellerName}? This cannot be undone.`)) return;
    try {
      const amount = parseFloat(tx.amount);
      const agentFee = (tx.buyerAgentId || tx.sellerAgentId) ? (amount * 0.0002) : 0; // 0.02%
      const adminFee = amount * 0.0005; // 0.05%
      const netAmount = amount - agentFee - adminFee;

      // 1. Pay Seller
      const sellerRef = doc(db, "users", tx.sellerId);
      const sellerSnap = await getDoc(sellerRef);
      if (sellerSnap.exists()) {
        const currentSellerWallet = sellerSnap.data().wallet || 0;
        await updateDoc(sellerRef, { wallet: currentSellerWallet + netAmount });
      }

      // 2. Pay Agent (if applicable)
      const agentIdToPay = tx.buyerAgentId || tx.sellerAgentId;
      if (agentIdToPay) {
        const agentRef = doc(db, "users", agentIdToPay);
        const agentSnap = await getDoc(agentRef);
        if (agentSnap.exists()) {
          const currentAgentWallet = agentSnap.data().wallet || 0;
          await updateDoc(agentRef, { wallet: currentAgentWallet + agentFee });
        }
      }

      // 3. Pay Admin
      const adminRef = doc(db, "users", "admin");
      const adminSnap = await getDoc(adminRef);
      if (adminSnap.exists()) {
        const currentAdminWallet = adminSnap.data().wallet || 0;
        await updateDoc(adminRef, { wallet: currentAdminWallet + adminFee });
      } else {
        // Create admin user if it doesn't exist? Or just error?
        // Let's assume admin exists as per ADMIN_ID
        await setDoc(adminRef, { wallet: adminFee, name: "Agrivestia Admin", email: "admin@agrivestia.com", isAgent: false }, { merge: true });
      }

      await updateDoc(doc(db, "market_transactions", tx.id), {
        status: "Completed",
        escrowReleased: true,
        completedAt: new Date().toISOString(),
        lastMessageAt: new Date().toISOString(),
        commissionBreakdown: { agentFee, adminFee, netAmount }
      });

      // Auto-system message
      let systemMsg = `✅ Funds Released. ₦${netAmount.toLocaleString()} moved to ${tx.sellerName}'s wallet.`;
      if (agentFee > 0) systemMsg += ` ₦${agentFee.toLocaleString()} commission paid to agent.`;
      systemMsg += ` Platform fee: ₦${adminFee.toLocaleString()}.`;

      await addDoc(collection(db, "market_transactions", tx.id, "messages"), {
        senderId: "SYSTEM",
        senderName: "Agrivestia System",
        text: systemMsg,
        timestamp: new Date().toISOString(),
        isSystem: true
      });

      setActiveTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, status: "Completed", escrowReleased: true, lastMessageAt: new Date().toISOString() } : t));
      showToast("success", "Funds Released", `Transaction Completed. Net ₦${netAmount.toLocaleString()} released.`);

      // Notifications
      sendTransactionEmail(tx.buyerId, tx.buyerName, tx.product, "Completed");
      sendTransactionEmail(tx.sellerId, tx.sellerName, tx.product, "Completed");
    } catch (error) { console.error(error); }
  };

  const cancelTransaction = async (tx) => {
    if (!window.confirm(`Are you sure you want to cancel this transaction${tx.isEscrow ? ' and refund the escrow to the buyer' : ''}? This action cannot be undone.`)) return;
    try {
      const isBuyer = tx.buyerId === userId;
      const amount = parseFloat(tx.amount) || 0;

      // 1. Refund Escrow to Buyer if applicable
      if (tx.isEscrow && amount > 0) {
        const buyerRef = doc(db, "users", tx.buyerId);
        const buyerSnap = await getDoc(buyerRef);
        if (buyerSnap.exists()) {
          const currentBuyerWallet = buyerSnap.data().wallet || 0;
          await updateDoc(buyerRef, { wallet: currentBuyerWallet + amount });
          if (isBuyer) {
            setWallet((currentBuyerWallet + amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,'));
          }
        }
      }

      // 2. Update Transaction Status
      await updateDoc(doc(db, "market_transactions", tx.id), {
        status: "Cancelled",
        cancelledBy: userId,
        cancelledAt: new Date().toISOString(),
        lastMessageAt: new Date().toISOString()
      });

      // 3. Increment cancelledTransactions for both users (reputation tracking)
      const incrementCancelled = async (uId) => {
        const uRef = doc(db, "users", uId);
        const uSnap = await getDoc(uRef);
        if (uSnap.exists()) {
          const prevCancelled = uSnap.data().cancelledTransactions || 0;
          await updateDoc(uRef, { cancelledTransactions: prevCancelled + 1 });
        }
      };
      await incrementCancelled(tx.buyerId);
      if (tx.sellerId) await incrementCancelled(tx.sellerId);

      // 4. System Message
      const refundMsg = tx.isEscrow ? ` ₦${amount.toLocaleString()} has been refunded to the buyer's wallet.` : '';
      await addDoc(collection(db, "market_transactions", tx.id, "messages"), {
        senderId: "SYSTEM",
        senderName: "Agrivestia System",
        text: `🚫 Transaction cancelled by ${name}.${refundMsg}`,
        timestamp: new Date().toISOString(),
        isSystem: true
      });

      setActiveTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, status: "Cancelled", lastMessageAt: new Date().toISOString() } : t));
      showToast("success", "Transaction Cancelled", "The transaction has been successfully cancelled.");
      setShowChat(false);

      // Notifications
      sendTransactionEmail(tx.buyerId, tx.buyerName, tx.product, "Cancelled");
      sendTransactionEmail(tx.sellerId, tx.sellerName, tx.product, "Cancelled");
    } catch (error) { console.error("Error cancelling transaction:", error); }
  };

  const handleNonEscrowCompletionRequest = async (tx) => {
    if (!window.confirm("Mark this transaction as completed? The seller will need to concur.")) return;
    try {
      await updateDoc(doc(db, "market_transactions", tx.id), {
        status: "Awaiting Concurrence",
        lastMessageAt: new Date().toISOString()
      });
      await addDoc(collection(db, "market_transactions", tx.id, "messages"), {
        senderId: "SYSTEM",
        senderName: "Agrivestia System",
        text: `⏳ ${name} marked the transaction as completed. Awaiting seller's concurrence.`,
        timestamp: new Date().toISOString(),
        isSystem: true
      });
      setActiveTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, status: "Awaiting Concurrence", lastMessageAt: new Date().toISOString() } : t));
      showToast("success", "Status Updated", "Waiting for the seller to agree to completion.");
    } catch (error) { console.error("Error updating transaction:", error); }
  };

  const incrementSuccessful = async (uId) => {
    try {
      const uRef = doc(db, "users", uId);
      const uSnap = await getDoc(uRef);
      if (uSnap.exists()) {
        const prevSuccess = uSnap.data().successfulTransactions || 0;
        await updateDoc(uRef, { successfulTransactions: prevSuccess + 1 });
      }
    } catch (error) { console.error("Error incrementing successful transactions:", error); }
  };

  const handleSellerAgreeCompletion = async (tx) => {
    if (!window.confirm("Agree that this transaction is successfully completed?")) return;
    try {
      await updateDoc(doc(db, "market_transactions", tx.id), {
        status: "Completed",
        completedAt: new Date().toISOString(),
        lastMessageAt: new Date().toISOString()
      });

      // Increment successful transactions for reputation
      await incrementSuccessful(tx.buyerId);
      if (tx.sellerId) await incrementSuccessful(tx.sellerId);

      await addDoc(collection(db, "market_transactions", tx.id, "messages"), {
        senderId: "SYSTEM",
        senderName: "Agrivestia System",
        text: `✅ ${name} agreed to completion. Transaction is now closed.`,
        timestamp: new Date().toISOString(),
        isSystem: true
      });
      setActiveTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, status: "Completed", lastMessageAt: new Date().toISOString() } : t));
      showToast("success", "Transaction Completed", "The transaction has been successfully closed.");

      // Notifications
      sendTransactionEmail(tx.buyerId, tx.buyerName, tx.product, "Completed");
      sendTransactionEmail(tx.sellerId, tx.sellerName, tx.product, "Completed");
    } catch (error) { console.error("Error agreeing to completion:", error); }
  };

  const handleSellerRejectCompletion = async (tx) => {
    const reason = window.prompt("Why are you rejecting the completion? (e.g., Payment not received)");
    if (!reason || !reason.trim()) return;
    try {
      await updateDoc(doc(db, "market_transactions", tx.id), {
        status: "Active",
        lastMessageAt: new Date().toISOString()
      });
      await addDoc(collection(db, "market_transactions", tx.id, "messages"), {
        senderId: "SYSTEM",
        senderName: "Agrivestia System",
        text: `❌ ${name} rejected completion. Reason: ${reason}`,
        timestamp: new Date().toISOString(),
        isSystem: true
      });
      setActiveTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, status: "Active", lastMessageAt: new Date().toISOString() } : t));
      showToast("info", "Completion Rejected", "The transaction is back to Active status.");
    } catch (error) { console.error("Error rejecting completion:", error); }
  };

  const handleReportDispute = async () => {
    if (!disputeReason || !disputeDescription.trim()) {
      alert("Please fill in all fields");
      return;
    }
    try {
      await addDoc(collection(db, "admin_tickets"), {
        type: "transaction_dispute",
        title: `Transaction Dispute - ${disputeTx.product}`,
        description: `Reason: ${disputeReason}\n\nDetails: ${disputeDescription}`,
        createdBy: userId,
        createdByName: name,
        createdByEmail: userEmail,
        status: "open",
        relatedTransactionId: disputeTx.id,
        transactionDetails: {
          product: disputeTx.product,
          amount: disputeTx.amount,
          partner: disputeTx.buyerId === userId ? disputeTx.sellerName : disputeTx.buyerName,
          partnerId: disputeTx.buyerId === userId ? disputeTx.sellerId : disputeTx.buyerId,
          status: disputeTx.status
        },
        messages: [{
          sender: "user",
          senderName: name,
          message: `Reporting dispute: ${disputeReason}`,
          timestamp: new Date().toISOString()
        }],
        timestamp: serverTimestamp()
      });

      setShowDisputeModal(false);
      setDisputeTx(null);
      setDisputeReason("");
      setDisputeDescription("");
      setToast({ type: "success", title: "Dispute Reported", msg: "Our team will review your dispute within 24 hours." });
      setTimeout(() => setToast(null), 4000);
    } catch (error) {
      console.error(error);
      alert("Failed to report dispute");
    }
  };

  const handleRegisterClient = async (e) => {
    e.preventDefault();
    const clientForm = e.target;
    const clientName = clientForm.clientName.value;
    const clientEmail = clientForm.clientEmail.value;
    const clientPhone = clientForm.clientPhone.value;

    if (!clientName || !clientEmail || !clientPhone) {
      showToast("error", "Validation", "Please fill in all client details.");
      return;
    }

    setIsSubmitting(true);
    try {
      const agentRef = doc(db, "users", userId);
      const agentSnap = await getDoc(agentRef);
      const agentData = agentSnap.data();

      const clientUserId = clientEmail.toLowerCase();
      const clientData = {
        name: clientName,
        email: clientEmail,
        phone: clientPhone,
        password: agentData.password,
        bankName: agentData.bankName || "",
        accountNumber: agentData.accountNumber || "",
        accountName: agentData.accountName || "",
        wallet: 0,
        managedBy: userId,
        isAgent: false,
        accountStatus: "active",
        activeProfile: "Buyer",
        createdAt: new Date().toISOString()
      };

      const clientSnap = await getDoc(doc(db, "users", clientUserId));
      if (clientSnap.exists()) {
        showToast("error", "Duplicate", "A user with this email already exists.");
        setIsSubmitting(false);
        return;
      }

      await setDoc(doc(db, "users", clientUserId), clientData);
      setShowRegisterClientModal(false);
      showToast("success", "Client Registered", `${clientName} has been added to your managed accounts.`);
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to register client.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeRequest = acceptBid; // Legacy mapping

  const navItems = [
    { icon: <LayoutDashboard size={18} />, label: "Dashboard", action: () => { window.location.href = "/dashboard"; } },
    { icon: <History size={18} />, label: "History", action: () => { window.location.href = "/history"; } },
    { icon: <Wallet size={18} />, label: "Wallet", action: () => { window.location.href = "/wallet"; } },
    { icon: <Settings size={18} />, label: "Settings", action: () => { window.location.href = "/settings"; } },
    { icon: <Store size={18} />, label: "Marketplace", active: true },
    { icon: <BookOpen size={18} />, label: "Health & News (Blog)", action: () => { window.location.href = "/blog"; } },
    { icon: <PhoneCall size={18} />, label: "Support", action: () => { window.location.href = "/contact"; } },
  ];

  const totalUnread = activeTransactions.reduce((acc, tx) => {
    const hasUnread = tx.lastMessageAt && (!tx.lastRead || !tx.lastRead[userId] || tx.lastMessageAt > tx.lastRead[userId]);
    return hasUnread ? acc + 1 : acc;
  }, 0);

  return (
    <div style={{ fontFamily: "var(--font-sans)", display: "flex", minHeight: "100vh", background: "var(--color-gray-50)" }}>
      <Helmet><title>Marketplace — Agrivestia</title></Helmet>

      {!emailVerified && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
          background: "#fff7ed", borderBottom: "1px solid #ffedd5",
          padding: "10px 20px", display: "flex", alignItems: "center",
          justifyContent: "center", gap: "12px", animation: "slideDown 0.4s ease"
        }}>
          <AlertCircle size={18} color="#f97316" />
          <span style={{ fontSize: "13px", color: "#9a3412", fontWeight: 600 }}>
            Your email is not verified. Please verify your email to enable all features.
          </span>
          <button
            onClick={() => window.location.href = `/verify_email?email=${encodeURIComponent(userEmail)}`}
            style={{
              background: "#f97316", color: "white", border: "none",
              padding: "4px 12px", borderRadius: "6px", fontSize: "12px",
              fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 4px rgba(249,115,22,0.2)"
            }}
          >
            Verify Now
          </button>
        </div>
      )}

      {toast && (
        <div className="toast" style={{ borderLeft: `4px solid ${toast.type === "success" ? "var(--color-success)" : "var(--color-danger)"}` }}>
          <div className={`toast-icon ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
            {toast.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          </div>
          <div className="toast-body"><div className="toast-title">{toast.title}</div><div className="toast-msg">{toast.msg}</div></div>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {show_mobile_menu && <div className="overlay" onClick={() => setMobileMenu(false)} />}

      <aside className={`sidebar ${show_mobile_menu ? "open" : ""}`} style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        <div style={{ padding: "24px 20px 16px", borderBottom: "1px solid var(--color-gray-100)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Leaf size={17} color="white" /></div>
            <span style={{ fontWeight: 800, fontSize: "17px", color: "var(--color-primary-dark)" }}>AGRIVESTIA</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--gradient-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "15px", color: "white" }}>{name[0]}</div>
            <div><div style={{ fontWeight: 600, fontSize: "13px" }}>{name}</div><div style={{ fontSize: "11px", color: "var(--color-gray-400)" }}>Market Participant</div></div>
          </div>
        </div>
        <div style={{ margin: "16px", padding: "14px 16px", background: "var(--color-primary-light)", borderRadius: "var(--radius-md)" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--color-primary)", letterSpacing: "0.05em" }}>{activeActingAs ? "CLIENT BALANCE" : "MY BALANCE"}</div>
          <div style={{ fontSize: "20px", fontWeight: 800 }}>₦{wallet}</div>
        </div>

        {isAgent && agentStatus === "approved" && (
          <div style={{ padding: "0 12px 16px", borderBottom: "1px solid var(--color-gray-100)" }}>
            <div style={{ padding: "0 8px 8px", fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)" }}>ACCOUNT CONTEXT</div>
            <div style={{ position: "relative" }}>
              <select
                className="input-field"
                style={{ marginBottom: "8px", fontSize: "13px", height: "40px", padding: "0 12px", background: activeActingAs ? "var(--color-primary-light)" : "white" }}
                value={activeActingAs || userId}
                onChange={(e) => setActiveActingAs(e.target.value === userId ? null : e.target.value)}
              >
                <option value={userId}>Me (Manager)</option>
                {managedClients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
              <button
                onClick={() => setShowRegisterClientModal(true)}
                style={{ width: "100%", padding: "8px", background: "white", border: "1px dashed var(--color-gray-300)", borderRadius: "var(--radius-md)", fontSize: "12px", color: "var(--color-gray-600)", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", cursor: "pointer" }}
              >
                <UserPlus size={14} /> Register New Client
              </button>
            </div>
          </div>
        )}
        <nav style={{ padding: "0 12px", flex: 1, overflowY: "auto" }}>
          {navItems.map((item, i) => (
            <button key={i} onClick={item.action} className={`sidebar-link ${item.active ? "active" : ""}`} style={{ width: "100%", border: "none", cursor: "pointer", marginBottom: "2px" }}>
              <span>{item.icon}</span><span style={{ fontSize: "14px" }}>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={{ padding: "20px", borderTop: "1px solid var(--color-gray-100)" }}>
          <button className="sidebar-link danger" onClick={() => { removeCookie("username"); window.location.href = "/login"; }} style={{ width: "100%", border: "none", cursor: "pointer" }}>
            <LogOut size={17} /><span>Log Out</span>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header style={{ background: "var(--color-white)", borderBottom: "1px solid var(--color-gray-100)", padding: "0 16px", minHeight: "64px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
          {/* Mobile Menu & Logo (Left) */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Mobile Hamburger */}
            <button onClick={() => setMobileMenu(true)} className="show-mobile" style={{ background: "none", border: "none", display: "none", color: "var(--color-gray-600)", padding: "4px" }}>
              <Menu size={24} />
            </button>

            <div className="show-mobile" style={{ display: "none", alignItems: "center", gap: "8px" }}
              onClick={() => window.location.href = "/"}>
              <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Leaf size={15} color="white" />
              </div>
              <span style={{ fontWeight: 800, fontSize: "14px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className="hide-mobile">
              <div style={{ fontSize: "16px", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
                Marketplace Manager
                {isAgent && agentStatus === "approved" && (
                  <span style={{ display: "flex", alignItems: "center", gap: "4px", padding: "2px 8px", background: "var(--gradient-accent)", color: "white", borderRadius: "12px", fontSize: "10px", fontWeight: 800, textTransform: "uppercase" }}>
                    <BadgeCheck size={10} /> Agent {agentId}
                  </span>
                )}
              </div>
              <div style={{ fontSize: "12px", color: "var(--color-gray-400)", display: "flex", alignItems: "center", gap: "4px" }}>
                {activeActingAs ? (
                  <span style={{ color: "var(--color-primary)", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px" }}>
                    <UserCheck size={12} /> Acting as: {name}
                  </span>
                ) : (
                  activeProfile === 'Buyer' ? 'Buying & Escrow' : 'Selling & Proposals'
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Bid Notifications Bell */}
            <div style={{ position: 'relative', color: 'var(--color-gray-400)' }}>
              <button
                onClick={() => {
                  const el = document.getElementById("incoming-bids");
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-ghost"
                style={{ padding: "8px", position: "relative", color: "var(--color-gray-600)" }}
                title={unreadBidsCount > 0 ? `${unreadBidsCount} new bid(s)` : "No new bids"}
              >
                <Bell size={20} />
                {unreadBidsCount > 0 && (
                  <span style={{ position: 'absolute', top: '2px', right: '2px', background: 'var(--color-danger)', color: 'white', fontSize: '10px', fontWeight: 900, borderRadius: '50%', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>
                    {unreadBidsCount > 9 ? "9+" : unreadBidsCount}
                  </span>
                )}
              </button>
            </div>

            {/* Activity Timeline (Transactions) */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="btn-ghost"
                style={{ padding: "8px", position: "relative", color: "var(--color-gray-600)", marginRight: "8px" }}
                title="Market Activity"
              >
                <MessageSquare size={20} />
                {totalUnread > 0 && (
                  <span style={{ position: 'absolute', top: '2px', right: '2px', background: 'var(--color-danger)', color: 'white', fontSize: '10px', fontWeight: 900, borderRadius: '50%', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>
                    {totalUnread}
                  </span>
                )}
              </button>

              {showNotifications && (
                <>
                  <div className="show-mobile overlay" onClick={() => setShowNotifications(false)} style={{ zIndex: 1000 }} />
                  <div className="card notification-panel" onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '40px', right: 0, width: '320px', padding: 0, zIndex: 1000, boxShadow: 'var(--shadow-xl)', border: '1px solid var(--color-gray-100)' }}>
                    <div style={{ padding: '16px', borderBottom: '1px solid var(--color-gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--color-gray-50)' }}>
                      <div style={{ fontWeight: 800, fontSize: '13px', color: 'var(--color-gray-900)' }}>Activity Timeline</div>
                      <button onClick={() => setShowNotifications(false)} style={{ background: 'none', border: 'none', color: 'var(--color-gray-400)' }}><X size={14} /></button>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {activeTransactions.length === 0 ? (
                        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-gray-400)', fontSize: '12px' }}>No recent activity</div>
                      ) : (
                        [...activeTransactions].sort((a, b) => (b.lastMessageAt || b.createdAt).localeCompare(a.lastMessageAt || a.createdAt)).map(tx => {
                          const hasUnread = tx.lastMessageAt && (!tx.lastRead || !tx.lastRead[userId] || tx.lastMessageAt > tx.lastRead[userId]);
                          return (
                            <div key={tx.id} onClick={() => { setActiveTx(tx); setShowChat(true); setShowNotifications(false); }} style={{ padding: '16px', borderBottom: '1px solid var(--color-gray-50)', cursor: 'pointer', background: hasUnread ? 'var(--color-primary-light)' : 'transparent', transition: 'all 0.2s' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <div style={{ fontWeight: 700, fontSize: '13px', color: hasUnread ? 'var(--color-primary-dark)' : 'var(--color-gray-900)' }}>{tx.product}</div>
                                {hasUnread && <span style={{ width: '8px', height: '8px', background: 'var(--color-primary)', borderRadius: '50%' }} />}
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--color-gray-500)', lineHeight: 1.4 }}>
                                {tx.status === 'Active' ? `Transaction active with ${tx.buyerId === userId ? tx.sellerName : tx.buyerName}` : `Funds released - Completed`}
                              </div>
                              <div style={{ fontSize: '10px', color: 'var(--color-gray-400)', marginTop: '8px' }}>{new Date(tx.lastMessageAt || tx.createdAt).toLocaleString()}</div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid var(--color-gray-100)' }}>
                      <button onClick={() => setShowNotifications(false)} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontWeight: 700, fontSize: '11px', cursor: 'pointer' }}>Close Panel</button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ display: 'flex', background: 'var(--color-gray-100)', borderRadius: '30px', padding: '3px', position: 'relative' }}>
              <button
                onClick={() => switchProfile("Buyer")}
                style={{
                  padding: '6px 16px', borderRadius: '20px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  background: activeProfile === "Buyer" ? "var(--color-white)" : "transparent",
                  color: activeProfile === "Buyer" ? "var(--color-primary)" : "var(--color-gray-500)",
                  boxShadow: activeProfile === "Buyer" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                  transition: 'all 0.2s'
                }}>Buyer</button>
              <button
                onClick={() => switchProfile("Seller")}
                style={{
                  padding: '6px 16px', borderRadius: '20px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  background: activeProfile === "Seller" ? "var(--color-white)" : "transparent",
                  color: activeProfile === "Seller" ? "var(--color-primary)" : "var(--color-gray-500)",
                  boxShadow: activeProfile === "Seller" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                  transition: 'all 0.2s'
                }}>Seller</button>
            </div>

            {activeProfile === "Buyer" && (
              <button className="btn-primary" onClick={() => setShowModal(true)} style={{ padding: "8px 16px", fontSize: "13px" }}><Plus size={15} /> <span className="hide-mobile">New Request</span></button>
            )}
            <button className="btn-ghost hide-mobile" onClick={() => window.location.href = "/central_market"} style={{ padding: "8px 16px", fontSize: "13px" }}><Globe size={15} /> Open Market</button>
          </div>
        </header>

        <div className="section-pad" style={{ padding: "clamp(16px, 5vw, 28px)" }}>
          <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: "1fr", gap: "28px" }}>

            {activeProfile === "Buyer" ? (
              <>
                {/* BUYER VIEW */}
                {/* Incoming Bids */}
                <div className="card" style={{ padding: 0 }}>
                  <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--color-gray-100)", display: "flex", alignItems: "center", gap: "8px" }}><Bell size={16} /> <div style={{ fontWeight: 700, fontSize: "14px" }}>Incoming Bids</div></div>
                  <div style={{ overflowX: "auto" }}>
                    {incomingBids.length === 0 ? <div style={{ padding: "30px", textAlign: "center", color: "var(--color-gray-400)" }}>No incoming bids</div> : (
                      <table className="data-table" style={{ minWidth: "600px" }}>
                        <thead><tr><th>Product</th><th>Buyer</th><th>Seller</th><th>Offer</th><th>Description</th><th>Contact</th><th>Action</th></tr></thead>
                        <tbody>{incomingBids.map(bid => {
                          const successRate = calculateSuccessRate(bid.sellerId);
                          return (
                            <tr key={bid.id}>
                              <td style={{ fontWeight: 600 }}>{bid.product}</td>
                              <td>{bid.buyerName}</td>
                              <td>
                                {bid.sellerName}
                                {successRate !== null && (
                                  <span style={{ marginLeft: "6px", fontSize: "11px", fontWeight: 700, color: successRate >= 80 ? "var(--color-success)" : successRate >= 50 ? "var(--color-warning)" : "var(--color-danger)" }}>
                                    ({successRate}%)
                                  </span>
                                )}
                              </td>
                              <td style={{ fontWeight: 700, color: "var(--color-success)" }}>{bid.priceOffer}</td>
                              <td style={{ fontSize: "12px", color: "var(--color-gray-600)", maxWidth: "200px" }}>{bid.notes || "—"}</td>
                              <td style={{ fontSize: "12px", color: "var(--color-gray-600)" }}>{bid.sellerContact || "—"}</td>
                              <td style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => acceptBid(bid.requestId, bid.id)} className="btn-primary" style={{ padding: "6px 12px", fontSize: "11px" }}>Accept</button>
                                <button onClick={() => rejectBid(bid.id)} className="btn-ghost" style={{ padding: "6px 12px", fontSize: "11px", color: "var(--color-danger)" }}>Reject</button>
                              </td>
                            </tr>
                          )
                        })}</tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* My Requests */}
                <div className="card" style={{ padding: 0 }}>
                  <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--color-gray-100)", display: "flex", alignItems: "center", gap: "8px" }}>
                    <Store size={16} color="var(--color-primary)" /><div style={{ fontWeight: 700, fontSize: "14px" }}>My Active Requests</div>
                  </div>
                  <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                    {items.length === 0 ? <div style={{ padding: "40px", textAlign: "center", color: "var(--color-gray-400)" }}>No active requests</div> : (
                      <table className="data-table" style={{ minWidth: "600px" }}>
                        <thead><tr><th>Product</th><th>Qty</th><th>Mode</th><th>Target</th><th>Escrow</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>{items.map(item => (
                          <tr key={item.id}>
                            <td style={{ fontWeight: 600 }}>{item.product}</td>
                            <td>{item.quantity}</td>
                            <td style={{ fontSize: '12px', fontWeight: 600 }}>{item.paymentMode}</td>
                            <td style={{ maxWidth: '200px', fontSize: '13px' }}>{item.price}</td>
                            <td style={{ color: "var(--color-success)", fontWeight: 700 }}>₦{parseFloat(item.escrowAmount).toLocaleString()}</td>
                            <td><span className="badge badge-primary">Active</span></td>
                            <td style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => deleteItem(item.id)} className="btn-ghost" style={{ padding: "6px 12px", fontSize: "11px", color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} title="Delete Request"><Trash2 size={14} /></button>
                              <button onClick={() => copyRequestLink(item.id)} className="btn-ghost" style={{ padding: "6px 12px", fontSize: "11px", color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }} title="Copy Market Link"><Link size={14} /></button>
                            </td>
                          </tr>
                        ))}</tbody>
                      </table>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* SELLER VIEW */}
                <div className="card" style={{ padding: 0 }}>
                  <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--color-gray-100)", display: "flex", alignItems: "center", gap: "8px" }}>
                    <Plus size={16} color="var(--color-primary)" /><div style={{ fontWeight: 700, fontSize: "14px" }}>My Sent Proposals</div>
                  </div>
                  <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                    {sentBids.length === 0 ? <div style={{ padding: "30px", textAlign: "center", color: "var(--color-gray-400)" }}>You haven't sent any bids yet. Visit the Open Market to find buyers.</div> : (
                      <table className="data-table" style={{ minWidth: "500px" }}>
                        <thead><tr><th>Product</th><th>Buyer</th><th>My Offer</th><th>Status</th></tr></thead>
                        <tbody>{sentBids.map(bid => (
                          <tr key={bid.id}>
                            <td style={{ fontWeight: 600 }}>{bid.product}</td>
                            <td>{bid.buyerName || 'Buyer'}</td>
                            <td style={{ fontWeight: 700, color: "var(--color-primary)" }}>{bid.priceOffer}</td>
                            <td><span className="badge badge-primary">{bid.status || 'Sent'}</span></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    )}
                  </div>
                </div>

                <div className="card" style={{ padding: '32px', textAlign: 'center', background: 'var(--gradient-primary)', color: 'white' }}>
                  <Globe size={32} style={{ marginBottom: '16px', opacity: 0.8 }} />
                  <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>Looking for more buyers?</h3>
                  <p style={{ opacity: 0.9, marginBottom: '24px', fontSize: '14px' }}>Browse purchase requests from farmers across the country and submit your best offers.</p>
                  <button onClick={() => window.location.href = "/central_market"} className="btn-secondary" style={{ padding: '12px 24px' }}>Open Central Market</button>
                </div>
              </>
            )}

            {/* SHARED SECTION: Active & Historic Transactions */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ display: "flex", borderBottom: "1px solid var(--color-gray-200)", background: "white", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0", overflow: "hidden" }}>
                <button
                  onClick={() => setTxTab('active')}
                  style={{ flex: 1, padding: "16px", background: txTab === 'active' ? "white" : "var(--color-gray-50)", border: "none", borderBottom: txTab === 'active' ? "3px solid var(--color-primary)" : "3px solid transparent", fontWeight: txTab === 'active' ? 800 : 600, color: txTab === 'active' ? "var(--color-primary)" : "var(--color-gray-500)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "all 0.2s" }}
                >
                  <CheckCircle2 size={16} /> Active Transactions
                </button>
                <button
                  onClick={() => setTxTab('history')}
                  style={{ flex: 1, padding: "16px", background: txTab === 'history' ? "white" : "var(--color-gray-50)", border: "none", borderBottom: txTab === 'history' ? "3px solid var(--color-primary)" : "3px solid transparent", fontWeight: txTab === 'history' ? 800 : 600, color: txTab === 'history' ? "var(--color-primary)" : "var(--color-gray-500)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "all 0.2s" }}
                >
                  <History size={16} /> Marketplace History
                </button>
              </div>

              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                {(() => {
                  const displayedTransactions = activeTransactions.filter(tx =>
                    txTab === 'active'
                      ? (tx.status === "Active" || tx.status === "Awaiting Concurrence")
                      : (tx.status === "Completed" || tx.status === "Cancelled")
                  );

                  if (displayedTransactions.length === 0) {
                    return <div style={{ padding: "30px", textAlign: "center", color: "var(--color-gray-400)" }}>{txTab === 'active' ? "No active transactions" : "No transaction history"}</div>;
                  }

                  return (
                    <table className="data-table" style={{ minWidth: "600px" }}>
                      <thead><tr><th>Product</th><th>Partner</th><th>Amount</th><th>Status</th><th>Action</th></tr></thead>
                      <tbody>{displayedTransactions.map(tx => {
                        const partnerId = tx.buyerId === userId ? tx.sellerId : tx.buyerId;
                        const partnerName = tx.buyerId === userId ? tx.sellerName : tx.buyerName;
                        const successRate = calculateSuccessRate(partnerId);

                        return (
                          <tr key={tx.id}>
                            <td style={{ fontWeight: 600 }}>{tx.product}</td>
                            <td>
                              {partnerName}
                              {successRate !== null && (
                                <span style={{ marginLeft: "6px", fontSize: "11px", fontWeight: 700, color: successRate >= 80 ? "var(--color-success)" : successRate >= 50 ? "var(--color-warning)" : "var(--color-danger)" }}>
                                  ({successRate}%)
                                </span>
                              )}
                            </td>
                            <td style={{ fontWeight: 700 }}>₦{parseFloat(tx.amount).toLocaleString()}</td>
                            <td><span className={`badge ${tx.status === 'Completed' ? 'badge-success' : tx.status === 'Cancelled' ? 'badge-danger' : 'badge-primary'}`}>{tx.status}</span></td>
                            <td style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <div style={{ position: 'relative' }}>
                                <button onClick={() => { setActiveTx(tx); setShowChat(true); }} className="btn-ghost" style={{ padding: "6px 12px", fontSize: "11px" }}><MessageSquare size={14} /> Chat</button>
                                {tx.lastMessageAt && (!tx.lastRead || !tx.lastRead[userId] || tx.lastMessageAt > tx.lastRead[userId]) && (
                                  <span style={{ position: 'absolute', top: '-5px', right: '-5px', width: '10px', height: '10px', background: 'var(--color-danger)', borderRadius: '50%', border: '2px solid white' }} />
                                )}
                              </div>
                              {tx.status === "Active" && tx.buyerId === userId && tx.isEscrow && (
                                <button onClick={() => releaseFunds(tx)} className="btn-primary" style={{ padding: "6px 12px", fontSize: "11px", background: 'var(--color-success)', borderColor: 'var(--color-success)' }}>Release Funds</button>
                              )}
                              {tx.status === "Active" && tx.buyerId === userId && !tx.isEscrow && (
                                <button onClick={() => handleNonEscrowCompletionRequest(tx)} className="btn-primary" style={{ padding: "6px 12px", fontSize: "11px", background: 'var(--color-success)', borderColor: 'var(--color-success)' }} title="Request seller to concur completion">Transaction Completed</button>
                              )}
                              {tx.status === "Awaiting Concurrence" && tx.sellerId === userId && (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button onClick={() => handleSellerAgreeCompletion(tx)} className="btn-primary" style={{ padding: "6px 12px", fontSize: "11px", background: 'var(--color-success)', borderColor: 'var(--color-success)' }}><Check size={14} /> Agree</button>
                                  <button onClick={() => handleSellerRejectCompletion(tx)} className="btn-ghost" style={{ padding: "6px 12px", fontSize: "11px", color: "var(--color-danger)", borderColor: "var(--color-danger)" }}><X size={14} /> Reject</button>
                                </div>
                              )}
                              {(tx.status === "Active" || tx.status === "Awaiting Concurrence") && (
                                <button onClick={() => cancelTransaction(tx)} className="btn-ghost" style={{ padding: "6px 12px", fontSize: "11px", color: "var(--color-warning)", borderColor: "var(--color-warning)" }}><XCircle size={14} /> Cancel</button>
                              )}
                              {(tx.status === "Active" || tx.status === "Awaiting Concurrence") && (
                                <button onClick={() => { setDisputeTx(tx); setShowDisputeModal(true); }} className="btn-ghost" style={{ padding: "6px 12px", fontSize: "11px", color: "var(--color-danger)", borderColor: "var(--color-danger)" }}><AlertTriangle size={14} /> Dispute</button>
                              )}
                            </td>
                          </tr>
                        )
                      })}</tbody>
                    </table>
                  );
                })()}
              </div>
            </div>

          </div>
        </div>

        {showModal && (
          <div className="overlay" onClick={() => setShowModal(false)} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div className="card" onClick={e => e.stopPropagation()} style={{ width: "95%", maxWidth: "500px", padding: "32px", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
                <h2 style={{ fontSize: "20px", fontWeight: 800 }}>New Purchase Request</h2>
                <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none" }}><X size={20} /></button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div className="input-group"><label className="input-label">Product Name</label><input className="input-field" name="product" placeholder="e.g. Cocoa" value={form.product} onChange={handleChange} /></div>

                <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '12px' }}>
                  <div className="input-group"><label className="input-label">Quantity</label><input className="input-field" type="number" name="quantityValue" placeholder="e.g. 50" value={form.quantityValue} onChange={handleChange} /></div>
                  <div className="input-group"><label className="input-label">Unit</label>
                    <select className="input-field" name="quantityUnit" value={form.quantityUnit} onChange={handleChange}>
                      <option>Tons</option><option>Bags</option><option>Kg</option><option>Litres</option><option>Boxes</option>
                    </select>
                  </div>
                </div>

                <div className="input-group"><label className="input-label">Unit Price (₦)</label><input className="input-field" type="number" name="unitPrice" placeholder="Price per unit" value={form.unitPrice} onChange={handleChange} /></div>

                <div className="input-group">
                  <label className="input-label">Payment Mode</label>
                  <select className="input-field" name="paymentMode" value={form.paymentMode} onChange={handleChange}>
                    <option>Agrivestia Escrow</option>
                    <option>LPO</option>
                    <option>Cash</option>
                    <option>Transfer</option>
                  </select>
                </div>

                {form.paymentMode === "Agrivestia Escrow" ? (
                  <div style={{ padding: '16px', background: 'var(--color-primary-light)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--color-primary)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--color-primary)', textTransform: 'uppercase' }}>Total Escrow to Lock</div>
                    <div style={{ fontSize: '24px', fontWeight: 900, color: 'var(--color-primary-dark)' }}>₦{parseFloat(form.escrowAmount).toLocaleString()}</div>
                    <div style={{ fontSize: '11px', color: 'var(--color-gray-500)', marginTop: '4px' }}>This exact amount will be deducted from your wallet and held in Agrivestia Escrow.</div>
                  </div>
                ) : (
                  <div style={{ padding: '16px', background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--color-gray-300)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--color-gray-600)', textTransform: 'uppercase' }}>Direct Payment via {form.paymentMode}</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-gray-700)', marginTop: '4px' }}>No funds will be locked in escrow.</div>
                    <div style={{ fontSize: '11px', color: 'var(--color-gray-500)', marginTop: '4px' }}>Transaction terms will be negotiated directly between you and the seller.</div>
                  </div>
                )}

                <div className="input-group"><label className="input-label">Quality Description</label><textarea className="input-field" name="description" rows={3} value={form.description} onChange={handleChange} placeholder="e.g. Grade A, well dried cocoa beans..." /></div>

                <button
                  className="btn-primary"
                  onClick={addItem}
                  disabled={!form.product || !form.quantityValue || isSubmitting}
                  style={{ padding: "16px", width: "100%", marginTop: '8px', opacity: (!form.product || !form.quantityValue || isSubmitting) ? 0.6 : 1 }}
                >
                  {isSubmitting ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                      <div className="spinner" style={{ width: "16px", height: "16px", border: "2px solid white", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                      Posting...
                    </div>
                  ) : `Post ${form.product || 'Request'}`}
                </button>
              </div>
            </div>
          </div>
        )}
        {showChat && activeTx && (
          <div className="overlay" onClick={() => setShowChat(false)} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div className="card" onClick={e => e.stopPropagation()} style={{ width: "95%", maxWidth: "600px", padding: 0, height: "80vh", display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--color-gray-100)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--color-primary)", color: "white" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px" }}>Chat with {activeTx.buyerId === userId ? activeTx.sellerName : activeTx.buyerName}</div>
                  <div style={{ fontSize: "11px", opacity: 0.8 }}>Order: {activeTx.product} — ₦{parseFloat(activeTx.amount).toLocaleString()}</div>
                </div>
                <button onClick={() => setShowChat(false)} style={{ background: "none", border: "none", color: "white" }}><X size={20} /></button>
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: "24px", display: "flex", flexDirection: "column", gap: "12px", background: "var(--color-gray-50)" }}>
                <TransactionChat txId={activeTx.id} currentUserId={userId} />
              </div>

              <div style={{ padding: "20px", borderTop: "1px solid var(--color-gray-100)", background: "white" }}>
                <ChatInput txId={activeTx.id} userId={userId} userName={name} />
              </div>
            </div>
          </div>
        )}

        {/* Dispute Modal */}
        {showDisputeModal && disputeTx && (
          <div className="overlay" onClick={() => setShowDisputeModal(false)} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div className="card" onClick={e => e.stopPropagation()} style={{ width: "95%", maxWidth: "500px", padding: "32px", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <h2 style={{ fontSize: "20px", fontWeight: 800, display: "flex", alignItems: "center", gap: "8px" }}>
                  <AlertTriangle size={20} color="var(--color-danger)" /> Report Dispute
                </h2>
                <button onClick={() => setShowDisputeModal(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
              </div>

              <div style={{ padding: "16px", background: "var(--color-danger-light)", borderRadius: "var(--radius-md)", marginBottom: "24px", border: "1px solid #fca5a5" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#7f1d1d", marginBottom: "4px" }}>Transaction Details</div>
                <div style={{ fontSize: "12px", color: "#991b1b" }}>
                  <div>Product: <strong>{disputeTx.product}</strong></div>
                  <div>Partner: <strong>{disputeTx.buyerId === userId ? disputeTx.sellerName : disputeTx.buyerName}</strong></div>
                  <div>Amount: <strong>₦{parseFloat(disputeTx.amount).toLocaleString()}</strong></div>
                  <div>Status: <strong>{disputeTx.status}</strong></div>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div className="input-group">
                  <label className="input-label">Reason for Dispute *</label>
                  <select value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} className="input-field" style={{ marginBottom: 0 }}>
                    <option value="">Select a reason...</option>
                    <option value="Product not received">Product not received</option>
                    <option value="Wrong product delivered">Wrong product delivered</option>
                    <option value="Poor quality">Poor quality</option>
                    <option value="Escrow not released">Escrow not released</option>
                    <option value="Payment not received">Payment not received</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">Detailed Description *</label>
                  <textarea
                    value={disputeDescription}
                    onChange={(e) => setDisputeDescription(e.target.value)}
                    placeholder="Please provide details about the dispute. Include dates, amounts, and any relevant information..."
                    rows={5}
                    className="input-field"
                    style={{ marginBottom: 0, resize: "none" }}
                  />
                </div>

                <div style={{ display: "flex", gap: "12px" }}>
                  <button onClick={() => setShowDisputeModal(false)} className="btn-ghost" style={{ flex: 1, justifyContent: "center", padding: "12px" }}>
                    Cancel
                  </button>
                  <button onClick={handleReportDispute} className="btn-primary" style={{ flex: 1, justifyContent: "center", padding: "12px", background: "var(--color-danger)", borderColor: "var(--color-danger)" }}>
                    <Send size={16} /> Report Dispute
                  </button>
                </div>

                <div style={{ padding: "12px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", fontSize: "12px", color: "var(--color-gray-600)", lineHeight: 1.4 }}>
                  <strong>Note:</strong> Our support team will investigate this dispute and contact both parties within 24 hours. Please provide accurate information to help us resolve this quickly.
                </div>
              </div>
            </div>
          </div>
        )}
        {showRegisterClientModal && (
          <div className="overlay" onClick={() => setShowRegisterClientModal(false)} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div className="card" onClick={e => e.stopPropagation()} style={{ width: "95%", maxWidth: "450px", padding: "32px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
                <div>
                  <h2 style={{ fontSize: "20px", fontWeight: 800 }}>Register New Client</h2>
                  <p style={{ fontSize: "12px", color: "var(--color-gray-400)", marginTop: "4px" }}>New account will be linked to your Agent ID</p>
                </div>
                <button onClick={() => setShowRegisterClientModal(false)} style={{ background: "none", border: "none" }}><X size={20} /></button>
              </div>

              <form onSubmit={handleRegisterClient} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div className="input-group">
                  <label className="input-label">Full Name</label>
                  <input className="input-field" name="clientName" placeholder="Client's Full Name" required />
                </div>
                <div className="input-group">
                  <label className="input-label">Email Address</label>
                  <input className="input-field" type="email" name="clientEmail" placeholder="client@example.com" required />
                </div>
                <div className="input-group">
                  <label className="input-label">Phone Number</label>
                  <input className="input-field" name="clientPhone" placeholder="08012345678" required />
                </div>

                <div style={{ padding: "12px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-gray-100)", marginTop: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--color-gray-600)", fontSize: "12px" }}>
                    <Lock size={14} /> <span>Security & Banking Inherited</span>
                  </div>
                  <div style={{ fontSize: "10px", color: "var(--color-gray-400)", marginTop: "4px", lineHeight: 1.4 }}>
                    The client will share your password and bank details for streamlined management and payouts.
                  </div>
                </div>

                <button type="submit" className="btn-primary" disabled={isSubmitting} style={{ padding: "14px", marginTop: "8px" }}>
                  {isSubmitting ? "Registering..." : "Add Client Account"}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const TransactionChat = ({ txId, currentUserId }) => {
  const [messages, setMessages] = useState([]);
  const chatEndRef = React.useRef(null);

  useEffect(() => {
    const q = query(
      collection(getFirestore(app), "market_transactions", txId, "messages"),
      orderBy("timestamp", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [txId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // Mark as read
    const markRead = async () => {
      await updateDoc(doc(getFirestore(app), "market_transactions", txId), {
        [`lastRead.${currentUserId}`]: new Date().toISOString()
      });
    };
    if (messages.length > 0) markRead();
  }, [messages, txId, currentUserId]);

  return (
    <>
      <div style={{ padding: '8px 12px', background: '#fef3c7', color: '#92400e', fontSize: '11px', fontWeight: 600, textAlign: 'center', borderRadius: 'var(--radius-md)', border: '1px solid #fde68a', marginBottom: '8px', zIndex: 10 }}>
        ⚠️ Please ensure that all transactions are communicated via the app to ensure close monitoring.
      </div>
      {messages.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--color-gray-400)', marginTop: '40px' }}>
          <MessageSquare size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
          <div style={{ fontSize: '13px' }}>No messages yet. Start the conversation!</div>
        </div>
      ) : (
        <>
          {messages.map((m, i) => {
            if (m.isSystem) {
              return (
                <div key={i} style={{
                  textAlign: 'center', margin: '16px 0', fontSize: '11px', color: 'var(--color-gray-500)',
                  padding: '8px 16px', background: 'var(--color-gray-100)', borderRadius: '20px', alignSelf: 'center'
                }}>
                  {m.text}
                </div>
              );
            }
            return (
              <div key={i} style={{
                alignSelf: m.senderId === currentUserId ? 'flex-end' : 'flex-start',
                maxWidth: '80%',
                padding: '10px 14px',
                borderRadius: '12px',
                background: m.senderId === currentUserId ? 'var(--color-primary)' : 'white',
                color: m.senderId === currentUserId ? 'white' : 'var(--color-gray-800)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                fontSize: '13px'
              }}>
                <div style={{ fontSize: '10px', opacity: 0.7, marginBottom: '2px', fontWeight: 700 }}>{m.senderName}</div>
                {m.text}
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </>
      )}
    </>
  );
};

const ChatInput = ({ txId, userId, userName }) => {
  const [msg, setMsg] = useState("");
  const sendMessage = async () => {
    if (!msg.trim()) return;
    try {
      const timestamp = new Date().toISOString();
      await addDoc(collection(getFirestore(app), "market_transactions", txId, "messages"), {
        senderId: userId,
        senderName: userName,
        text: msg,
        timestamp: timestamp
      });
      await updateDoc(doc(getFirestore(app), "market_transactions", txId), {
        lastMessageAt: timestamp
      });
      setMsg("");
    } catch (e) { console.error(e); }
  };

  return (
    <div style={{ display: "flex", gap: "10px" }}>
      <input
        className="input-field"
        style={{ marginBottom: 0 }}
        placeholder="Type a message..."
        value={msg}
        onChange={e => setMsg(e.target.value)}
        onKeyPress={e => e.key === 'Enter' && sendMessage()}
      />
      <button onClick={sendMessage} className="btn-primary" style={{ padding: "0 20px" }}>Send</button>
    </div>
  );
};
